import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CoverageMeter, type CoverageMeterCoverageState, type CoverageMeterProcessGroup } from '@xxtract/ui';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { useCoverage } from '../hooks/useCoverage';
import { formatFetchedAt, stepLabel } from '../format';
import type { CoverageData, ProcessCoverageEntry } from '../processes.api';

/**
 * Story 2.4 (FR13/FR14) — het event-dekkingsoverzicht: per proces per canonieke stap gedekt /
 * niet-gedekt, plus de "N van 10" fysiek-doelbeeld-samenvatting en zichtbaar gemaakte ongemapte
 * procesactiviteit. `/processes/coverage`.
 */
export function Dekking() {
  const { t, i18n } = useTranslation();
  const coverageQuery = useCoverage();

  if (coverageQuery.isError) {
    return <ApiErrorMessage error={coverageQuery.error} onRetry={() => coverageQuery.refetch()} />;
  }

  const data = coverageQuery.data?.data;

  function toGroup(entry: ProcessCoverageEntry): CoverageMeterProcessGroup {
    return {
      name: entry.name,
      processLabel: t(`processes.names.${entry.name}`),
      // De labelset is er sinds 2026-08-17: `stepLabel` vertaalt de zestien stapcodes die de bron
      // werkelijk levert, en valt terug op de ruwe code voor een stap die er ooit bijkomt.
      steps: entry.steps.map((step) => ({
        canonicalStep: step.canonicalStep,
        stepLabel: stepLabel(step.canonicalStep, t),
        coverageState: step.coverageState as CoverageMeterCoverageState,
        count: step.count,
        lastEventLabel: step.lastEventAt != null ? formatFetchedAt(step.lastEventAt, i18n.language) : undefined,
        statusLabel: t(`processes.coverageState.${step.coverageState}`),
      })),
    };
  }

  function summaryLabel(coverage: CoverageData): string {
    return t('processes.dekking.summary', { covered: coverage.physicalTargetSummary.covered, denominator: coverage.physicalTargetSummary.denominator });
  }

  return (
    <div className="space-y-5" data-testid="coverage-page">
      <header>
        <h1 className="text-xl font-bold tracking-tight text-[var(--foreground)]">{t('processes.dekking.title')}</h1>
        <p className="text-xs text-[var(--foreground-muted)]">{t('processes.dekking.subtitle')}</p>
        {data ? (
          <p aria-live="polite" className="text-xs text-[var(--foreground-muted)]" data-testid="coverage-updated-at">
            {t('processes.updatedAt', { time: formatFetchedAt(data.fetchedAt, i18n.language) })}
          </p>
        ) : null}
      </header>

      {coverageQuery.isLoading ? <div className="h-40 animate-pulse rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--background-muted)]" data-testid="coverage-loading" /> : null}

      {data ? (
        <>
          {data.sources.procesManagement === 'unavailable' ? (
            <p className="text-xs text-[var(--info-text)]" role="status">{t('processes.degraded.title')}</p>
          ) : null}

          <Card data-testid="coverage-meter-card">
            <CardHeader>
              <CardTitle>{t('processes.dekking.meterTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <CoverageMeter groups={data.processes.map(toGroup)} physicalTargetSummary={data.physicalTargetSummary} summaryLabel={summaryLabel(data)} />
            </CardContent>
          </Card>

          <Card data-testid="coverage-unmapped-card">
            <CardHeader>
              <CardTitle>{t('processes.dekking.unmappedTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              {data.unmapped.length === 0 ? (
                <p className="text-xs text-[var(--foreground-muted)]" data-testid="coverage-unmapped-empty">{t('processes.dekking.unmappedEmpty')}</p>
              ) : (
                <ul className="flex flex-wrap gap-2" data-testid="coverage-unmapped-list" role="list">
                  {data.unmapped.map((code) => (
                    <li className="rounded-[var(--radius-control)] bg-[var(--info-light)] px-2 py-1 text-xs text-[var(--info-text)]" data-testid="coverage-unmapped-code" key={code}>
                      {code}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
